import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toDanishPath, toEnglishPath } from '../lib/routeMapping';

const LanguageContext = createContext();

const translations = {
  da: {
    nav: {
      home: 'Forside',
      udredning: 'Udredning',
      adhd: 'ADHD',
      autisme: 'Autisme',
      medicinsk_behandling: 'Medicinsk behandling',
      about: 'Om os',
      psykiatriske_lidelser: 'Øvrige psykiatriske diagnoser',
      angst: 'Angstlidelser',
      ocd: 'OCD',
      depression: 'Depression',
      ptsd: 'PTSD og belastningsreaktioner',
      personlighedsforstyrrelser: 'Personlighedsforstyrrelser',
      prices: 'Priser',
      contact: 'Kontakt',
      vidensbank: 'Vidensbank',
      book: 'Book tid'
    },
    common: {
      cta_primary: 'Book indledende samtale',
      cta_secondary: 'Læs om forløbet',
      contact_us: 'Kontakt os',
      read_more: 'Læs mere',
      back: 'Tilbage',
      loading: 'Henter indhold...',
      error_loading: 'Der skete en fejl under indlæsning af indholdet.',
      faq_title: 'Ofte stillede spørgsmål',
      gallery_title: 'Se vores klinik og rammer',
      contact_cta_title: 'Er du klar til at tage det næste skridt?',
      contact_cta_text: 'Kontakt os i dag for en uforpligtende samtale om, hvordan vi kan hjælpe dig eller din pårørende.',
      contact_cta_btn: 'Kontakt os nu'
    },
    home: {
      hero_badge: 'Specialiseret privat psykiatri',
      hero_title: 'Udredning og behandling med høj faglighed og mennesket i centrum',
      hero_desc: 'Vi tilbyder grundig udredning og målrettet behandling inden for ADHD, autisme og andre psykiatriske lidelser – med mulighed for at gennemføre et udredningsforløb inden for 3-4 uger fra første samtale.',
      services_title: 'Hvad kan vi hjælpe med?',
      services_subtitle: 'Vi tilbyder specialiserede forløb tilpasset dine individuelle behov.',
      why_title: 'Hvorfor vælge Nordisk Psykiatri?',
      why_subtitle: 'Vi forener høj faglig ekspertise med en dyb menneskelig forståelse for at give dig det bedste forløb.',
      why_1_title: 'Specialiserede forløb',
      why_1_desc: 'Fokus på ADHD, autisme og andre psykiatriske lidelser.',
      why_2_title: 'Psykolog og psykiater samlet',
      why_2_desc: 'Tæt samarbejde mellem autoriseret psykolog og psykiater gennem udredning og behandling.',
      why_3_title: 'Udredning på 3-4 uger',
      why_3_desc: 'Vi tilbyder hurtig opstart med mulighed for at gennemføre et udredningsforløb inden for 3-4 uger.',
      why_4_title: 'Evidensbaseret tilgang',
      why_4_desc: 'Behandling baseret på nyeste forskning og kliniske retningslinjer.',
      why_5_title: 'Diskretion og tryghed',
      why_5_desc: 'Fortrolige rammer med fokus på tillid og sikkerhed.',
      why_6_title: 'Sammenhæng i forløb',
      why_6_desc: 'Vi følger dig hele vejen – fra udredning til behandling.',
      process_title: 'Sådan foregår et forløb',
      process_subtitle: 'En tryg og struktureret vej fra første kontakt til afsluttende rapport.',
      process_step_1: 'Kontakt',
      process_step_1_desc: 'Du kontakter os og beskriver kort din situation.',
      process_step_2: 'Forsamtale',
      process_step_2_desc: 'Vi taler om dine udfordringer og forventninger.',
      process_step_3: 'Udredning',
      process_step_3_desc: 'Grundig og systematisk udredningsproces.',
      process_step_4: 'Tilbagemelding',
      process_step_4_desc: 'Vi gennemgår resultaterne sammen med dig.',
      process_step_5: 'Behandling',
      process_step_5_desc: 'Målrettet behandling og opfølgning tilpasset dig.',
      about_title: 'Om klinikken',
      about_subheading: 'Vores faglige fundament',
      about_text_1: 'Nordisk Psykiatri er en privat psykiatrisk klinik beliggende i rolige og diskrete omgivelser. Vi tilbyder udredning og behandling med tid, nærvær og høj faglighed.',
      about_text_2: 'Vi arbejder ud fra de nyeste videnskabelige retningslinjer og anvender internationalt anerkendte testværktøjer. Men vigtigst af alt møder vi dig som det hele menneske, du er.',
      reviews_title: 'Det siger vores klienter',
      reviews_subtitle: 'Udtalelser fra personer og familier, der har gennemgået et forløb hos os.',
      reviews: [
        { text: "Jeg følte mig utrolig tryg og mødt i øjenhøjde gennem hele mit ADHD-udredningsforløb. Det har givet mig en helt ny forståelse af mig selv.", author: "Sofie, 28 år" },
        { text: "Professionelt, grundigt og ekstremt omsorgsfuldt. Vores søns autismeudredning blev håndteret med en varme og tålmodighed, vi ikke har oplevet før.", author: "Thomas, far til Lucas på 9 år" },
        { text: "Klar struktur, fantastiske lokaler og specialister, der virkelig ved, hvad de taler om. Kan varmt anbefales til alle, der søger svar.", author: "Mette, 42 år" },
        { text: "Efter mange års søgen fik jeg endelig den rette hjælp og forståelse. Forløbet har været livsforandrende for mig.", author: "Jonas, 35 år" }
      ]
    },
    udredning: {
      hero_title: 'Udredning af ADHD og autisme',
      hero_desc: 'En grundig og professionel udredning er nøglen til at forstå dine styrker og udfordringer. Vi guider dig trygt igennem hele processen.',
      who_title: 'Hvem henvender udredningen sig til?',
      who_desc: 'Udredning henvender sig til voksne, unge og børn, der oplever udfordringer i hverdagen, som kan være relateret til ADHD, autisme eller andre neurodivergerende træk. Det kan være problemer med koncentration, struktur, sociale relationer, sanseoverfølsomhed eller tilbagevendende mental træthed.',
      who_bullet_1: 'Voksne, der søger svar på livslange udfordringer',
      who_bullet_2: 'Børn og unge, der har brug for støtte i skole og fritid',
      who_bullet_3: 'Pårørende, der ønsker bedre forståelse og redskaber',
      who_bullet_4: 'Arbejdspladser eller uddannelsesinstitutioner, der søger rådgivning',
      process_title: 'Processen trin for trin',
      process_desc: 'Vi har tilrettelagt vores udredningsforløb, så det er så overskueligt, forudsigeligt og trygt som muligt.',
      step_1_title: '1. Indledende samtale',
      step_1_desc: 'Vi kortlægger din historik, dine nuværende udfordringer og vurderer, om der er grundlag for en udredning.',
      step_2_title: '2. Diagnostiske interviews',
      step_2_desc: 'Vi anvender semistrukturerede interviews (f.eks. DIVA-5 for ADHD eller ADI-R for autisme) for at dykke ned i symptomerne.',
      step_3_title: '3. Kognitiv og klinisk testning',
      step_3_desc: 'Vi undersøger opmærksomhed, eksekutive funktioner og eventuelle sansemæssige eller sociale mønstre.',
      step_4_title: '4. Tværfaglig konference',
      step_4_desc: 'Vores specialister gennemgår alle data sammen for at sikre en præcis og nuanceret diagnostisk vurdering.',
      step_5_title: '5. Tilbagemelding & handlingsplan',
      step_5_desc: 'Du modtager en grundig mundtlig og skriftlig gennemgang af resultaterne samt konkrete anbefalinger til din hverdag.'
    },
    adhd: {
      hero_title: 'ADHD-udredning og rådgivning',
      hero_desc: 'ADHD er ikke en hindring, men en anden måde at fungere på. Vi hjælper dig med at finde strukturen og roen i en travl hverdag.',
      intro_title: 'Hvad er ADHD?',
      intro_desc: 'ADHD (Attention Deficit Hyperactivity Disorder) er en neuroudviklingsforstyrrelse, der primært påvirker opmærksomhed, impulsstyring og aktivitetsniveau. Det viser sig forskelligt fra person til person og kan ændre sig gennem livet. Mange voksne lever med udiagnosticeret ADHD, hvilket kan føre til kronisk stress, lavt selvværd og udbrændthed.',
      symptoms_title: 'Typiske symptomer',
      symptoms_desc: 'Symptomer på ADHD kan opdeles i tre hovedområder, men styrken og kombinationen varierer betydeligt:',
      sym_1_title: 'Opmærksomhedsvanskeligheder',
      sym_1_desc: 'Svært ved at fastholde fokus, organisere opgaver, tendens til at glemme aftaler og blive let afledt af ydre stimuli.',
      sym_2_title: 'Impulsivitet',
      sym_2_desc: 'At handle før man tænker, afbryde andre, utålmodighed og hurtige, nogle gange uovervejede beslutninger.',
      sym_3_title: 'Hyperaktivitet / Indre uro',
      sym_3_desc: 'Fysisk rastløshed hos børn, som hos voksne ofte transformeres til en konstant, invaliderende indre uro eller tankemylder.',
      sym_4_title: 'Eksekutive funktioner',
      sym_4_desc: 'Udfordringer med at planlægge, igangsætte og fuldføre komplekse opgaver samt regulere følelser og energiniveau.',
      challenges_title: 'Hverdagsudfordringer',
      challenges_desc: 'At leve med ADHD kan gøre helt almindelige hverdagsopgaver uoverskuelige. Det kan handle om at overholde tidsfrister, strukturere en arbejdsuge, opretholde sunde rutiner eller regulere sine følelser i pressede situationer. Vores udredning har fokus på at identificere netop dine specifikke udfordringer, så vi kan give dig de rette værktøjer.',
      process_title: 'Udredningsforløb for ADHD',
      process_desc: 'Vores ADHD-udredning følger de nationale kliniske retningslinjer og sikrer en grundig, evidensbaseret vurdering.'
    },
    autisme: {
      hero_title: 'Autismeudredning og forståelse',
      hero_desc: 'Autisme handler om at opleve verden på en unik måde. Vi skaber rammerne for dybdegående forståelse og personlig trivsel.',
      intro_title: 'Hvad er autisme?',
      intro_desc: 'Autismespektrumtilstande (AST) dækker over en række medfødte neurobiologiske forskelle i, hvordan hjernen bearbejder information, sanser og sociale indtryk. Autisme er ikke en sygdom, der skal helbredes, men en anden kognitiv profil. En udredning kan give en dyb lettelse og en forklaring på, hvorfor man ofte har følt sig anderledes eller misforstået.',
      char_title: 'Typiske karakteristika',
      char_desc: 'Autisme viser sig på mange forskellige måder, men omfatter ofte særlige mønstre inden for:',
      char_1_title: 'Social kommunikation',
      char_1_desc: 'Udfordringer med at afkode uforudsigelige sociale spilleregler, kropssprog, ironi eller at indgå i uformel smalltalk.',
      char_2_title: 'Forudsigelighed og struktur',
      char_2_desc: 'Et stærkt behov for faste rammer, rutiner og detaljeret planlægning. Uforudsete ændringer kan skabe stor indre stress.',
      char_3_title: 'Særlige interesser & fokus',
      char_3_desc: 'Evnen til intens fordybelse i specifikke emner eller detaljer, hvilket ofte ledsages af stor viden og ekspertise.',
      char_4_title: 'Sensorisk sensitivitet',
      char_4_desc: 'Over- eller undersensitivitet over for lys, lyde, lugte, berøring eller tætpakkede forsamlinger.',
      challenges_title: 'Hverdagsudfordringer',
      challenges_desc: 'Mange autistiske personer bruger enorme mængder energi på at "maskere" deres udfordringer for at passe ind i neurotypiske rammer. Dette kan føre til ekstrem mental udmattelse, angst eller depression. Vi hjælper med at afdække dine sensoriske og kognitive grænser, så du kan indrette dit liv mere skånsomt og bæredygtigt.',
      process_title: 'Udredningsforløb for autisme',
      process_desc: 'Vores autismeudredning er baseret på guldstandard-værktøjer som ADOS-2 og ADI-R, udført af erfarne specialister.'
    },
    about: {
      hero_title: 'Om os og vores værdier',
      hero_desc: 'Med specialisterne bag klinikken. Vi arbejder hver dag for at skabe et trygt og professionelt fundament for din udredning og behandling.',
      history_title: 'Klinikkens historie',
      history_desc_1: 'Nordisk Psykiatri blev etableret med et ønske om at gøre op med lange ventetider og upersonlige forløb. Vi ønskede at skabe en privat specialistklinik, hvor der er tid til det enkelte menneske, og hvor faglighed og kvalitet aldrig går på kompromis.',
      history_desc_2: 'Klinikken er grundlagt af erfarne specialister med mange års erfaring inden for psykiatrisk udredning og behandling. Vi har samlet et team med høj faglighed og et fælles ønske om at tilbyde psykiatri, vi selv ville vælge.',
      values_title: 'Vores værdier',
      values_desc: 'Vores arbejde hviler på fire grundlæggende principper, som gennemsyrer alt, hvad vi gør.',
      val_1_title: 'Faglig integritet',
      val_1_desc: 'Vi holder os konstant opdateret på den nyeste forskning og arbejder udelukkende ud fra evidensbaserede metoder.',
      val_2_title: 'Menneskelig varme',
      val_2_desc: 'Vi møder alle mennesker med nysgerrighed, respekt og empati. Hos os er du aldrig blot et journalnummer.',
      val_3_title: 'Tryghed & ro',
      val_3_desc: 'Vi skaber fysiske og mentale rammer, der minimerer stress og fremmer en følelse af sikkerhed og forudsigelighed.',
      val_4_title: 'Individuelt fokus',
      val_4_desc: 'Vi tilpasser altid vores forløb til din unikke livssituation, ressourcer og behov.',
      mission_title: 'Vores mission',
      mission_desc_1: 'Vores mission er at hjælpe neurodivergente mennesker og andre med psykiske udfordringer til at forstå sig selv bedre og leve et liv med større trivsel, mening og frihed.',
      mission_desc_2: 'Vi kombinerer specialiseret viden med menneskelig indsigt og arbejder målrettet for at skabe forløb, der gør en reel forskel – i trygge, rolige og professionelle rammer.',
      team_title: 'Mød vores specialister',
      team_subtitle: 'Et erfarent og dedikeret team af psykologer og psykiatere.',
      quality_eyebrow: 'Kvalitet & patientsikkerhed',
      quality_title: 'Høj kvalitet og patientsikkerhed – altid',
      quality_1: 'Hos Nordisk Psykiatri er kvalitet og patientsikkerhed grundlaget for alle patientforløb. Vi er en registreret sundhedsvirksomhed under tilsyn af Styrelsen for Patientsikkerhed og arbejder i overensstemmelse med gældende dansk lovgivning om autorisation, journalføring, tavshedspligt og patientrettigheder.',
      quality_2: 'Alle vores behandlere er autoriserede sundhedspersoner med de nødvendige faglige kompetencer, og deres autorisation kan verificeres via Autorisationsregisteret.',
      quality_3: 'Vi baserer vores udredning og behandling på aktuel forskningsbaseret viden og nationale faglige anbefalinger. Ved udredning af blandt andet ADHD og autismespektrumforstyrrelser anvender vi anerkendte og internationalt validerede udredningsredskaber, herunder DIVA-5, AAA, ADOS-2 og RAADS-R, når de indgår som en relevant del af den kliniske vurdering.',
      quality_4: 'Fortrolighed er en selvfølge. Alle person- og helbredsoplysninger behandles sikkert og fortroligt i overensstemmelse med GDPR og sundhedslovens bestemmelser, og vi anvender krypterede systemer til både journalføring og kommunikation.',
      cta_title: 'Klar til at tage det første skridt?',
      cta_desc: 'Book en indledende samtale, og lad os finde ud af, hvordan vi bedst kan hjælpe dig.',
      cta_btn: 'Book en indledende samtale'
    },
    prices: {
      hero_title: 'Priser og ydelser',
      hero_desc: 'Vi ønsker fuld gennemsigtighed. Her finder du en oversigt over vores priser på udredning og rådgivning.',
      disclaimer_title: 'Vigtig information om priser',
      disclaimer_desc: 'Alle priser er faste og inkluderer alle nødvendige tests, interviews, rapporter og tilbagemeldinger. Der kommer ingen uforudsete ekstraudgifter undervejs. Flere sundhedsforsikringer dækker helt eller delvist udredningsforløb hos os – vi anbefaler, at du kontakter dit forsikringsselskab inden opstart.',
      contact_cta_prices: 'Har du spørgsmål til priser eller betaling?',
      contact_cta_prices_desc: 'Vi tilbyder i visse tilfælde mulighed for ratebetaling. Kontakt os for at høre nærmere om mulighederne.'
    },
    contact: {
      hero_title: 'Kontakt klinikken',
      hero_desc: 'Vi sidder klar til at hjælpe dig. Skriv eller ring til os for en uforpligtende snak eller for at booke en tid.',
      info_title: 'Kontaktoplysninger',
      form_title: 'Send os en besked',
      form_desc: 'Udfyld formularen nedenfor, så vender vi tilbage til dig inden for 24 timer på hverdage.',
      form_name: 'Navn',
      form_phone: 'Telefonnummer',
      form_email: 'E-mail',
      form_desc_label: 'Beskrivelse af din henvendelse',
      form_submit: 'Send besked',
      form_submitting: 'Sender...',
      form_success: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.',
      form_error: 'Der skete en fejl under afsendelsen. Prøv venligst igen eller ring til os.',
      hours_title: 'Åbningstider',
      hours_mon_thu: 'Mandag - Torsdag',
      hours_sun: 'Søndag',
      hours_fri_sat: 'Fredag - Lørdag',
      hours_closed: 'Lukket',
      map_title: 'Her finder du os',
      address_label: 'Adresse',
      phone_label: 'Telefon',
      email_label: 'E-mail'
    },
    not_found: {
      title: 'Siden blev ikke fundet (404)',
      desc: 'Beklager, men den side du leder efter findes ikke eller er blevet flyttet.',
      btn: 'Gå til forsiden'
    },
    forbidden: {
      title: 'Ingen adgang (403)',
      desc: 'Du har ikke tilladelse til at tilgå denne side.',
      btn: 'Gå til forsiden'
    },
    vidensbank: {
      title: 'Vidensbank',
      subtitle: 'Dyk ned i vores artikler og få dybere indsigt i ADHD, autisme og mental sundhed.',
      search_placeholder: 'Søg i artikler...',
      all_categories: 'Alle kategorier',
      read_time: 'Læsetid',
      read_more: 'Læs artikel',
      no_results: 'Ingen artikler fundet, der matcher din søgning.',
      back_to_list: 'Tilbage til vidensbanken'
    }
  },
  sv: {
    nav: {
      home: 'Hem',
      udredning: 'Utredning',
      adhd: 'ADHD',
      autisme: 'Autism',
      medicinsk_behandling: 'Medicinsk behandling',
      about: 'Om oss',
      psykiatriske_lidelser: 'Andra psykiatriska problemställningar',
      angst: 'Ångestsyndrom',
      ocd: 'OCD',
      depression: 'Depression',
      ptsd: 'PTSD och belastningsreaktioner',
      personlighedsforstyrrelser: 'Personlighetsstörningar',
      prices: 'Priser',
      contact: 'Kontakt',
      vidensbank: 'Kunskap',
      book: 'Boka tid'
    },
    common: {
      cta_primary: 'Boka inledande samtal',
      cta_secondary: 'Läs om förloppet',
      contact_us: 'Kontakta oss',
      read_more: 'Läs mer',
      back: 'Tillbaka',
      loading: 'Laddar innehåll...',
      error_loading: 'Ett fel uppstod när innehållet skulle laddas.',
      faq_title: 'Vanliga frågor',
      gallery_title: 'Se vår klinik och miljöer',
      contact_cta_title: 'Är du redo att ta nästa steg?',
      contact_cta_text: 'Kontakta oss idag för ett förutsättningslöst samtal om hur vi kan hjälpa dig eller din närstående.',
      contact_cta_btn: 'Kontakta oss nu'
    },
    home: {
      hero_badge: 'Specialiserad privat psykiatri',
      hero_title: 'Utredning och behandling med hög facklig kompetens och människan i centrum',
      hero_desc: 'Vi erbjuder noggrann utredning och riktad behandling inom ADHD, autism och andra psykiatriska tillstånd.',
      services_title: 'Vad kan vi hjälpa till med?',
      services_subtitle: 'Vi erbjuder specialiserade förlopp anpassade efter dina individuella behov.',
      why_title: 'Varför välja Nordisk Psykiatri?',
      why_subtitle: 'Vi förenar hög professionell expertis med en djup mänsklig förståelse för att ge dig det bästa förloppet.',
      why_1_title: 'Specialiserade förlopp',
      why_1_desc: 'Fokus på ADHD, autism och andra psykiatriska tillstånd.',
      why_2_title: 'Erfarna och legitimerade behandlare',
      why_2_desc: 'Legitimerade specialistläkare i psykiatri och kliniska psykologer.',
      why_3_title: 'Kort väntetid',
      why_3_desc: 'Vi erbjuder snabb uppstart med möjlighet att genomföra ett utredningsförlopp inom 3-4 veckor.',
      why_4_title: 'Evidensbaserat tillvägagångssätt',
      why_4_desc: 'Behandling baserad på den senaste forskningen och kliniska riktlinjer.',
      why_5_title: 'Diskretion och trygghet',
      why_5_desc: 'Konfidentiella ramar med fokus på tillit och säkerhet.',
      why_6_title: 'Sammanhang i förloppet',
      why_6_desc: 'Vi följer dig hela vägen – från utredning till behandling.',
      process_title: 'Så går ett förlopp till',
      process_subtitle: 'En trygg och strukturerad väg från första kontakt till avslutande rapport.',
      process_step_1: 'Kontakt',
      process_step_1_desc: 'Du kontaktar oss och beskriver kort din situation.',
      process_step_2: 'Inledande samtal',
      process_step_2_desc: 'Vi talar om dina utmaningar och förväntningar.',
      process_step_3: 'Utredning',
      process_step_3_desc: 'Noggrann och systematisk utredningsprocess.',
      process_step_4: 'Återkoppling',
      process_step_4_desc: 'Vi går igenom resultaten tillsammans med dig.',
      process_step_5: 'Behandling',
      process_step_5_desc: 'Riktad behandling och uppföljning anpassad efter dig.',
      about_title: 'Om kliniken',
      about_subheading: 'Vår vetenskapliga grund',
      about_text_1: 'Nordisk Psykiatri är en privat psykiatrisk klinik belägen i lugna och diskreta miljöer. Vi erbjuder utredning och behandling med tid, närvaro och hög facklig kompetens.',
      about_text_2: 'Vi arbetar utifrån de senaste vetenskapliga riktlinjerna och använder internationellt erkända testverktyg. Men viktigast av allt är att vi möter dig som den hela människa du är.',
      reviews_title: 'Det säger våra klienter',
      reviews_subtitle: 'Uttalanden från personer och familjer som har genomgått en utredning hos oss.',
      reviews: [
        { text: "Jag kände mig otroligt trygg och bemött i ögonhöjd under hela min ADHD-utredning. Det har givet mig en helt ny förståelse för mig själv.", author: "Sofie, 28 år" },
        { text: "Professionellt, noggrant och extremt omsorgsfullt. Vår sons autismutredning hanterades med en värme och ett tålamod som vi inte upplevt tidigare.", author: "Thomas, pappa till Lucas, 9 år" },
        { text: "Tydlig struktur, fantastiska lokaler och specialister som verkligen vet vad de pratar om. Rekommenderas varmt till alla som söker svar.", author: "Mette, 42 år" },
        { text: "Efter många års sökande fick jag äntligen rätt hjälp och förståelse. Utredningen har varit livsförändrande för mig.", author: "Jonas, 35 år" }
      ]
    },
    udredning: {
      hero_title: 'Utredning av ADHD och autism',
      hero_desc: 'En noggrann och professionell utredning är nyckeln till att förstå dina styrkor och utmaningar. Vi guidar dig tryggt genom hela processen.',
      who_title: 'Vem vänder sig utredningen till?',
      who_desc: 'Utredningen vänder sig till vuxna, ungdomar och barn som upplever utmaningar i vardagen som kan vara relaterade till ADHD, autism eller andra neurodivergenta drag. Det kan handla om problem med koncentration, struktur, sociala relationer, sensorisk känslighet eller återkommande mental trötthet.',
      who_bullet_1: 'Vuxna som söker svar på livslånga utmaningar',
      who_bullet_2: 'Barn och ungdomar som behöver stöd i skolan och på fritiden',
      who_bullet_3: 'Anhöriga som önskar bättre förståelse och verktyg',
      who_bullet_4: 'Arbetsplatser eller utbildningsinstitutioner som söker rådgivning',
      process_title: 'Processen steg för steg',
      process_desc: 'Vi har lagt upp vår utredningsprocess så att den ska vara så överskådlig, förutsägbar och trygg som möjligt.',
      step_1_title: '1. Inledande samtal',
      step_1_desc: 'Vi kartlägger din historik, dina nuvarande utmaningar och bedömer om det finns grund för en utredning.',
      step_2_title: '2. Diagnostiska intervjuer',
      step_2_desc: 'Vi använder semistrukturerade intervjuer (t.ex. DIVA-5 för ADHD eller ADI-R för autism) för att fördjupa oss i symptomen.',
      step_3_title: '3. Kognitiva och kliniska tester',
      step_3_desc: 'Vi undersöker uppmärksamhet, exekutiva funktioner samt eventuella sensoriska eller sociala mönster.',
      step_4_title: '4. Tvärvetenskaplig konferens',
      step_4_desc: 'Våra specialister går igenom all data tillsammans för att säkerställa en exakt och nyanserad diagnostisk bedömning.',
      step_5_title: '5. Återkoppling & handlingsplan',
      step_5_desc: 'Du får en grundlig muntlig och skriftlig genomgång av resultaten samt konkreta rekommendationer för din vardag.'
    },
    adhd: {
      hero_title: 'ADHD-utredning och rådgivning',
      hero_desc: 'ADHD är inget hinder, utan ett annat sätt att fungera på. Vi hjälper dig att hitta strukturen och lugnet i en hektisk vardag.',
      intro_title: 'Vad är ADHD?',
      intro_desc: 'ADHD (Attention Deficit Hyperactivity Disorder) är en neurobiologisk funktionsnedsättning som främst påverkar uppmärksamhet, impulsstyrning och aktivitetsnivå. Det visar sig olika från person till person och kan förändras genom livet. Många voksna lever med odiagnostiserad ADHD, vilket kan leda till kronisk stress, låg självkänsla och utbrändhet.',
      symptoms_title: 'Typiska symptom',
      symptoms_desc: 'Symptom på ADHD kan delas in i tre huvudområden, men styrkan och kombinationen varierar avsevärt:',
      sym_1_title: 'Uppmärksamhetssvårigheter',
      sym_1_desc: 'Svårt att behålla fokus, organisera uppgifter, tendens att glömma avtal och bli lätt distraherad av yttre stimuli.',
      sym_2_title: 'Impulsivitet',
      sym_2_desc: 'Att handla innan man tänker, avbryta andra, otålighet och snabba, ibland oöverlagda beslut.',
      sym_3_title: 'Hyperaktivitet / Inre oro',
      sym_3_desc: 'Fysisk rastlöshet hos barn, som hos vuxna ofta omvandlas till en konstant, handikappande inre oro eller tankesus.',
      sym_4_title: 'Exekutiva funktioner',
      sym_4_desc: 'Utmaningar med att planera, initiera och slutföra komplexa uppgifter samt reglera känslor och energinivå.',
      challenges_title: 'Vardagsutmaningar',
      challenges_desc: 'Att leva med ADHD kan göra helt vanliga vardagssysslor övermäktiga. Det kan handla om att hålla tidsgränser, strukturera en arbetsvecka, upprätthålla hälsosamma rutiner eller reglera sina känslor i stressiga situationer. Vår utredning fokuserar på att identifiera just dina specifika utmaningar så att vi kan ge dig rätt verktyg.',
      process_title: 'Utredningsförlopp för ADHD',
      process_desc: 'Vår ADHD-utredning följer nationella kliniska riktlinjer och säkerställer en noggrann, evidensbaserad bedömning.'
    },
    autisme: {
      hero_title: 'Autismutredning och förståelse',
      hero_desc: 'Autism handlar om att uppleva världen på ett unikt sätt. Vi skapar förutsättningar för djupgående förståelse och personlig trivsel.',
      intro_title: 'Vad är autism?',
      intro_desc: 'Autismspektrumtillstånd (AST) omfattar en rad medfödda neurobiologiska skillnader i hur hjärnan bearbetar information, sinnesintryck och sociala interaktioner. Autism är inte en sjukdom som ska botas, utan en annan kognitiv profil. En utredning kan ge en djup lättnad och en förklaring till varför man ofta har känt sig annorlunda eller missförstådd.',
      char_title: 'Typiska egenskaper',
      char_desc: 'Autism visar sig på många olika sätt, men omfattar ofta särskilda mönster inom:',
      char_1_title: 'Social kommunikation',
      char_1_desc: 'Utmaningar med att avkoda oförutsägbara sociala spelregler, kroppsspråk, ironi eller att delta i informellt småprat.',
      char_2_title: 'Förutsägbarhet och struktur',
      char_2_desc: 'Ett starkt behov av fasta ramar, rutiner och detaljerad planering. Oförutsedda förändringar kan skapa stor inre stress.',
      char_3_title: 'Särskilda intressen & fokus',
      char_3_desc: 'Förmågan till intensiv fördjupning i specifika ämnen eller detaljer, vilket ofta åtföljs av stor kunskap och expertis.',
      char_4_title: 'Sensorisk känslighet',
      char_4_desc: 'Över- eller underkänslighet för ljus, ljud, lukter, beröring eller trånga folksamlingar.',
      challenges_title: 'Vardagsutmaningar',
      challenges_desc: 'Många autistiska personer lägger enorma mängder energi på att "maskera" sina utmaningar för att passa in i neurotypiska miljöer. Detta kan leda till extrem mental utmattning, ångest eller depression. Vi hjälper till att kartlägga dina sensoriska och kognitiva gränser så att du kan anpassa ditt liv på ett mer skonsamt och hållbart sätt.',
      process_title: 'Utredningsförlopp för autism',
      process_desc: 'Vår autismutredning är baserad på guldstandardverktyg som ADOS-2 och ADI-R, utförd av erfarna specialister.'
    },
    about: {
      hero_title: 'Om oss och våra värderingar',
      hero_desc: 'Med specialisterna bakom kliniken. Vi arbetar varje dag för att skapa en trygg och professionell grund för din utredning och behandling.',
      history_title: 'Klinikens historia',
      history_desc_1: 'Nordisk Psykiatri grundades med en önskan om att erbjuda ett alternativ till långa väntetider och opersonliga utredningar. Vi ville skapa en privat specialistklinik där det finns tid för individen och där facklig kompetens och kvalitet aldrig kompromissas.',
      history_desc_2: 'Kliniken är grundad av erfarna specialister med mångårig erfarenhet inom psykiatrisk utredning och behandling. Vi har samlat ett team med hög facklig kompetens och en gemensam önskan om att erbjuda psykiatri vi själva skulle välja.',
      values_title: 'Våra värderingar',
      values_desc: 'Vårt arbete vilar på fyra grundläggande principer, som genomsyrar allt vi gör.',
      val_1_title: 'Professionell integritet',
      val_1_desc: 'Vi håller oss ständigt uppdaterade om den senaste forskningen och arbetar uteslutande utifrån evidensbaserade metoder.',
      val_2_title: 'Mänsklig värme',
      val_2_desc: 'Vi möter alla människor med nyfikenhet, respekt och empati. Hos oss är du aldrig bara ett journalnummer.',
      val_3_title: 'Trygghet & Lugn',
      val_3_desc: 'Vi skapar fysiska och mentala ramar som minimerar stress och främjar en känsla av säkerhet och förutsägbarhet.',
      val_4_title: 'Individuellt fokus',
      val_4_desc: 'Vi anpassar alltid våra utredningar efter din unika livssituation, resurser och behov.',
      mission_title: 'Vår mission',
      mission_desc_1: 'Vår mission är att hjälpa neurodivergenta människor och andra med psykiska utmaningar att förstå sig själva bättre och leva ett liv med större trivsel, mening och frihet.',
      mission_desc_2: 'Vi kombinerar specialistkunskap med mänsklig insikt och arbetar målinriktat för att skapa förlopp som gör en verklig skillnad – i trygga, lugna och professionella ramar.',
      team_title: 'Möt våra specialister',
      team_subtitle: 'Ett erfaret och engagerat team av psykologer och psykiatriker.',
      quality_eyebrow: 'Kvalitet & patientsäkerhet',
      quality_title: 'Hög kvalitet och patientsäkerhet – alltid',
      quality_1: 'Hos Nordisk Psykiatri är kvalitet och patientsäkerhet grunden för alla patientförlopp. Vi är en registrerad vårdgivare under tillsyn av Styrelsen for Patientsikkerhed och arbetar i enlighet med gällande dansk lagstiftning om auktorisation, journalföring, tystnadsplikt och patienträttigheter.',
      quality_2: 'Alla våra behandlare är legitimerade vårdgivare med nödvändig facklig kompetens, och deras legitimation kan verifieras via Autorisationsregisteret.',
      quality_3: 'Vi baserar vår utredning och behandling på aktuell forskningsbaserad kunskap och nationella fackliga rekommendationer. Vid utredning av bland annat ADHD och autism använder vi erkända och internationellt validerade utredningsverktyg, inklusive DIVA-5, AAA, ADOS-2 och RAADS-R, när de ingår som en relevant del av den kliniska bedömningen.',
      quality_4: 'Sekretess är en självklarhet. Alla person- och hälsouppgifter behandlas säkert och konfidentiellt i enlighet med GDPR och hälso- och sjukvårdslagen, och vi använder krypterade system för både journalföring och kommunikation.',
      cta_title: 'Redo att ta det första steget?',
      cta_desc: 'Boka ett inledande samtal, så tar vi reda på hur vi bäst kan hjälpa dig.',
      cta_btn: 'Boka ett inledande samtal'
    },
    prices: {
      hero_title: 'Priser och tjänster',
      hero_desc: 'Vi vill ha full transparens. Här hittar du en översikt över våra priser för utredning och rådgivning.',
      disclaimer_title: 'Viktig information om priser',
      disclaimer_desc: 'Alla priser är fasta och inkluderar alla nödvändiga tester, intervjuer, rapporter och återkopplingar. Det tillkommer inga oförutsedda extrakostnader. Flera sjukvårdsförsäkringar täcker helt eller delvis utredningar hos oss – vi rekommenderar att du kontaktar ditt försäkringsbolag innan start.',
      contact_cta_prices: 'Har du frågor om priser eller betalning?',
      contact_cta_prices_desc: 'Vi erbjuder i vissa fall möjlighet till delbetalning. Kontakta oss för att höra mer om alternativen.'
    },
    contact: {
      hero_title: 'Kontakta kliniken',
      hero_desc: 'Vi är redo att hjälpa dig. Skriv eller ring till oss för ett förutsättningslöst samtal eller för att boka tid.',
      info_title: 'Kontaktuppgifter',
      form_title: 'Skicka ett meddelande',
      form_desc: 'Fyll i formuläret nedan så återkommer vi till dig inom 24 timmar på vardagar.',
      form_name: 'Namn',
      form_phone: 'Telefonnummer',
      form_email: 'E-post',
      form_desc_label: 'Beskrivning av ditt ärende',
      form_submit: 'Skicka meddelande',
      form_submitting: 'Skickar...',
      form_success: 'Tack för ditt meddelande! Vi har tagit emot det och återkommer så snart som möjligt.',
      form_error: 'Ett fel uppstod när meddelandet skulle skickas. Försök igen eller ring oss.',
      hours_title: 'Öppettider',
      hours_mon_thu: 'Måndag - Torsdag',
      hours_sun: 'Söndag',
      hours_fri_sat: 'Fredag - Lördag',
      hours_closed: 'Stängt',
      map_title: 'Här hittar du oss',
      address_label: 'Adress',
      phone_label: 'Telefon',
      email_label: 'E-post'
    },
    not_found: {
      title: 'Sidan hittades inte (404)',
      desc: 'Tyvärr finns inte sidan du letar efter eller så har den flyttats.',
      btn: 'Gå till startsidan'
    },
    forbidden: {
      title: 'Ingen tillgång (403)',
      desc: 'Du har inte behörighet att besöka denna sida.',
      btn: 'Gå till startsidan'
    },
    vidensbank: {
      title: 'Kunskapsbank',
      subtitle: 'Dyk ner i våra artiklar och få djupare insikt i ADHD, autism och mental hälsa.',
      search_placeholder: 'Sök bland artiklar...',
      all_categories: 'Alla kategorier',
      read_time: 'Lästid',
      read_more: 'Läs artikel',
      no_results: 'Inga artiklar hittades som matchar din sökning.',
      back_to_list: 'Tillbaka till kunskapsbanken'
    }
  },
  en: {
    nav: {
      home: 'Home',
      udredning: 'Assessment',
      adhd: 'ADHD',
      autisme: 'Autism',
      medicinsk_behandling: 'Medical treatment',
      about: 'About us',
      psykiatriske_lidelser: 'Other psychiatric conditions',
      angst: 'Anxiety disorders',
      ocd: 'OCD',
      depression: 'Depression',
      ptsd: 'PTSD and stress reactions',
      personlighedsforstyrrelser: 'Personality disorders',
      prices: 'Prices',
      contact: 'Contact',
      vidensbank: 'Knowledge base',
      book: 'Book appointment'
    },
    common: {
      cta_primary: 'Book initial consultation',
      cta_secondary: 'Read about the process',
      contact_us: 'Contact us',
      read_more: 'Read more',
      back: 'Back',
      loading: 'Loading content...',
      error_loading: 'An error occurred while loading content.',
      faq_title: 'Frequently asked questions',
      gallery_title: 'See our clinic and facilities',
      contact_cta_title: 'Ready to take the next step?',
      contact_cta_text: 'Contact us today for an informal conversation about how we can help you or your loved one.',
      contact_cta_btn: 'Contact us now'
    },
    home: {
      hero_badge: 'Specialized private psychiatry',
      hero_title: 'Assessment and treatment with high clinical expertise and the individual at the center',
      hero_desc: 'We offer comprehensive assessments and targeted treatment for ADHD, autism, and other psychiatric conditions – with the option to complete an assessment within 3-4 weeks from the initial consultation.',
      services_title: 'How can we help?',
      services_subtitle: 'We offer specialized clinical pathways tailored to your individual needs.',
      why_title: 'Why choose Nordisk Psykiatri?',
      why_subtitle: 'We combine high professional expertise with deep human empathy to provide you with the best care.',
      why_1_title: 'Specialized pathways',
      why_1_desc: 'Focus on ADHD, autism, and other psychiatric conditions.',
      why_2_title: 'Experienced and certified specialists',
      why_2_desc: 'Licensed psychiatrists and clinical psychologists working in close collaboration.',
      why_3_title: 'Short waiting time',
      why_3_desc: 'We offer prompt initiation with the option to complete an assessment within 3-4 weeks.',
      why_4_title: 'Evidence-based approach',
      why_4_desc: 'Care grounded in current scientific research and clinical guidelines.',
      why_5_title: 'Discretion and trust',
      why_5_desc: 'A confidential and calm setting prioritizing security and trust.',
      why_6_title: 'Continuity of care',
      why_6_desc: 'We support you throughout – from assessment to treatment.',
      process_title: 'How the process works',
      process_subtitle: 'A clear and structured path from initial contact to the final clinical report.',
      process_step_1: 'Contact',
      process_step_1_desc: 'Reach out to us and briefly describe your situation.',
      process_step_2: 'Initial consultation',
      process_step_2_desc: 'We discuss your challenges, history, and expectations.',
      process_step_3: 'Assessment',
      process_step_3_desc: 'Thorough and systematic diagnostic process.',
      process_step_4: 'Feedback',
      process_step_4_desc: 'We review the results and conclusions together with you.',
      process_step_5: 'Treatment',
      process_step_5_desc: 'Targeted treatment and follow-up tailored to you.',
      about_title: 'About the clinic',
      about_subheading: 'Our clinical foundation',
      about_text_1: 'Nordisk Psykiatri is a private psychiatric clinic situated in quiet and discreet surroundings. We provide assessment and treatment with ample time, presence, and clinical excellence.',
      about_text_2: 'We work according to the latest scientific guidelines and employ internationally recognized diagnostic instruments. Most importantly, we meet you as a whole human being.',
      reviews_title: 'What our clients say',
      reviews_subtitle: 'Testimonials from individuals and families who have completed an assessment with us.',
      reviews: [
        { text: "I felt exceptionally safe and met at eye level throughout my ADHD assessment. It has given me an entirely new understanding of myself.", author: "Sofie, 28 years old" },
        { text: "Professional, thorough, and remarkably caring. Our son's autism assessment was handled with a warmth and patience we haven't experienced before.", author: "Thomas, father of Lucas, 9 years old" },
        { text: "Clear structure, wonderful facilities, and specialists who truly know their field. Warmly recommended to anyone seeking clarity.", author: "Mette, 42 years old" },
        { text: "After many years of searching, I finally received the right help and understanding. The assessment has been life-changing for me.", author: "Jonas, 35 years old" }
      ]
    },
    udredning: {
      hero_title: 'Assessment of ADHD and Autism',
      hero_desc: 'A thorough and professional assessment is key to understanding your strengths and challenges. We guide you safely through the entire process.',
      who_title: 'Who is the assessment for?',
      who_desc: 'Our assessment is intended for adults, adolescents, and children who experience everyday challenges that may be related to ADHD, autism, or other neurodivergent traits. These may include difficulties with concentration, executive function, social relationships, sensory sensitivity, or recurring mental fatigue.',
      who_bullet_1: 'Adults seeking answers to lifelong challenges',
      who_bullet_2: 'Children and adolescents in need of support in school and daily life',
      who_bullet_3: 'Relatives seeking better understanding and practical tools',
      who_bullet_4: 'Workplaces or educational institutions seeking professional guidance',
      process_title: 'The process step by step',
      process_desc: 'We have structured our assessment pathway to be as transparent, predictable, and supportive as possible.',
      step_1_title: '1. Initial consultation',
      step_1_desc: 'We map your history and current challenges and assess whether there is grounds for a formal assessment.',
      step_2_title: '2. Diagnostic interviews',
      step_2_desc: 'We use semi-structured diagnostic interviews (e.g. DIVA-5 for ADHD or ADOS-2 / ADI-R for autism) to evaluate symptoms.',
      step_3_title: '3. Cognitive & clinical testing',
      step_3_desc: 'We evaluate attention, executive functions, and relevant sensory or social interaction patterns.',
      step_4_title: '4. Multidisciplinary conference',
      step_4_desc: 'Our specialists review all clinical data together to ensure a precise, nuanced diagnostic conclusion.',
      step_5_title: '5. Feedback & action plan',
      step_5_desc: 'You receive a thorough verbal and written review of the results, along with actionable recommendations for everyday life.'
    },
    adhd: {
      hero_title: 'ADHD assessment and guidance',
      hero_desc: 'ADHD is not a limitation, but a different way of functioning. We help you find structure and calm in a demanding daily life.',
      intro_title: 'What is ADHD?',
      intro_desc: 'ADHD (Attention Deficit Hyperactivity Disorder) is a neurodevelopmental condition that primarily affects attention, impulse control, and activity levels. It manifests differently from person to person and may change across a lifespan. Many adults live with undiagnosed ADHD, which can result in chronic stress, low self-esteem, and burnout.',
      symptoms_title: 'Common symptoms',
      symptoms_desc: 'Symptoms of ADHD are generally grouped into three main categories, though intensity and combination vary substantially:',
      sym_1_title: 'Attention difficulties',
      sym_1_desc: 'Challenges maintaining focus, organizing tasks, tendency to forget commitments, and being easily distracted by external stimuli.',
      sym_2_title: 'Impulsivity',
      sym_2_desc: 'Acting before thinking, interrupting conversations, impatience, and making hasty, sometimes unconsidered decisions.',
      sym_3_title: 'Hyperactivity / Inner restlessness',
      sym_3_desc: 'Physical restlessness in children, which in adults often transforms into a persistent inner tension or racing thoughts.',
      sym_4_title: 'Executive functions',
      sym_4_desc: 'Difficulties planning, initiating, and completing complex tasks, as well as regulating emotions and energy levels.',
      challenges_title: 'Everyday challenges',
      challenges_desc: 'Living with ADHD can make ordinary everyday tasks feel overwhelming. This may involve meeting deadlines, structuring a workweek, maintaining healthy routines, or regulating emotions during stressful situations. Our assessment identifies your specific challenges to provide you with the right tools.',
      process_title: 'ADHD assessment process',
      process_desc: 'Our ADHD assessments follow national clinical guidelines and ensure a comprehensive, evidence-based evaluation.'
    },
    autisme: {
      hero_title: 'Autism assessment and understanding',
      hero_desc: 'Autism is about experiencing the world in a unique way. We create the framework for in-depth understanding and personal well-being.',
      intro_title: 'What is autism?',
      intro_desc: 'Autism spectrum conditions (ASC) encompass a range of innate neurobiological differences in how the brain processes information, sensory inputs, and social interactions. Autism is not an illness to be cured, but a distinct cognitive profile. An assessment can bring profound relief and clarity about lifelong feelings of being misunderstood.',
      char_title: 'Key characteristics',
      char_desc: 'Autism manifests in diverse ways, but often involves distinctive patterns within:',
      char_1_title: 'Social communication',
      char_1_desc: 'Challenges decoding unwritten social norms, body language, irony, or engaging in informal small talk.',
      char_2_title: 'Predictability and routine',
      char_2_desc: 'A strong need for clear structure, routines, and detailed planning. Unforeseen changes may cause significant internal stress.',
      char_3_title: 'Special interests & hyperfocus',
      char_3_desc: 'The capacity for deep immersion in specific subjects or details, often accompanied by extensive knowledge and expertise.',
      char_4_title: 'Sensory sensitivity',
      char_4_desc: 'Hyper- or hypo-sensitivity to lights, sounds, odors, touch, or crowded environments.',
      challenges_title: 'Everyday challenges',
      challenges_desc: 'Many autistic individuals expend vast energy "masking" their difficulties to fit neurotypical expectations, which can lead to exhaustion, anxiety, or depression. We help uncover your sensory and cognitive boundaries so you can structure life more sustainably.',
      process_title: 'Autism assessment process',
      process_desc: 'Our autism assessments use gold-standard tools such as ADOS-2 and ADI-R, administered by experienced clinicians.'
    },
    about: {
      hero_title: 'About us and our values',
      hero_desc: 'Meet the specialists behind the clinic. Every day, we work to provide a safe, respectful, and professional foundation for your assessment and treatment.',
      history_title: 'Our history',
      history_desc_1: 'Nordisk Psykiatri was founded with a mission to move away from long waiting lists and impersonal clinical pathways. We wanted to build a private specialist clinic where there is genuine time for the individual and where quality is never compromised.',
      history_desc_2: 'The clinic was established by experienced specialists with many years in psychiatric diagnostics and treatment. We have gathered a team with high clinical integrity and a shared desire to deliver psychiatry we would choose for ourselves.',
      values_title: 'Our values',
      values_desc: 'Our work is anchored in four core principles that guide everything we do.',
      val_1_title: 'Professional integrity',
      val_1_desc: 'We stay continuously updated on the latest scientific research and work strictly within evidence-based methods.',
      val_2_title: 'Human warmth',
      val_2_desc: 'We meet every client with genuine curiosity, respect, and empathy. With us, you are never just a case number.',
      val_3_title: 'Safety & Calm',
      val_3_desc: 'We provide physical and mental spaces that reduce stress and foster a sense of security and predictability.',
      val_4_title: 'Individual focus',
      val_4_desc: 'We always adapt our assessments and treatment to your unique life situation, resources, and personal goals.',
      mission_title: 'Our mission',
      mission_desc_1: 'Our mission is to help neurodivergent individuals and those facing mental health challenges understand themselves better and live with greater well-being, purpose, and autonomy.',
      mission_desc_2: 'We combine specialized clinical knowledge with human insight, working purposefully to create pathways that make a genuine difference in calm, professional surroundings.',
      team_title: 'Meet our specialists',
      team_subtitle: 'An experienced and dedicated team of licensed psychologists and psychiatrists.',
      quality_eyebrow: 'Quality & patient safety',
      quality_title: 'High quality and patient safety – always',
      quality_1: 'At Nordisk Psykiatri, quality and patient safety form the cornerstone of every patient pathway. We are a registered healthcare provider supervised by the Danish Patient Safety Authority and operate in strict compliance with current legislation regarding licensing, medical records, confidentiality, and patient rights.',
      quality_2: 'All our clinicians are certified healthcare professionals with the required clinical credentials, verifiable through the official Register of Authorized Healthcare Professionals.',
      quality_3: 'We base our assessments and treatments on current research-based knowledge and national clinical guidelines. In assessments of ADHD and autism spectrum conditions, we use validated instruments such as DIVA-5, AAA, ADOS-2, and RAADS-R where clinically relevant.',
      quality_4: 'Confidentiality is paramount. All personal and health data is processed securely in compliance with GDPR and healthcare privacy regulations, using encrypted systems for both record keeping and communication.',
      cta_title: 'Ready to take the first step?',
      cta_desc: 'Book an initial consultation, and let us find out how we can best support you.',
      cta_btn: 'Book an initial consultation'
    },
    prices: {
      hero_title: 'Prices and services',
      hero_desc: 'We believe in full transparency. Here you will find an overview of our fees for assessments, treatment, and consultation.',
      disclaimer_title: 'Important pricing information',
      disclaimer_desc: 'All fees are fixed and include all necessary tests, interviews, written reports, and feedback sessions. There are no unexpected extra charges. Several private health insurances cover assessment pathways in full or in part – we advise contacting your insurer beforehand.',
      contact_cta_prices: 'Have questions about pricing or payment?',
      contact_cta_prices_desc: 'In certain cases, we offer installment payment options. Contact us to hear more about available arrangements.'
    },
    contact: {
      hero_title: 'Contact the clinic',
      hero_desc: 'We are ready to assist you. Call or write to us for an informal conversation or to book an appointment.',
      info_title: 'Contact details',
      form_title: 'Send us a message',
      form_desc: 'Complete the form below and we will get back to you within 24 hours on business days.',
      form_name: 'Name',
      form_phone: 'Phone number',
      form_email: 'Email',
      form_desc_label: 'Description of your inquiry',
      form_submit: 'Send message',
      form_submitting: 'Sending...',
      form_success: 'Thank you for your inquiry! We have received your message and will respond as soon as possible.',
      form_error: 'An error occurred while sending your message. Please try again or call us directly.',
      hours_title: 'Opening hours',
      hours_mon_thu: 'Monday - Thursday',
      hours_sun: 'Sunday',
      hours_fri_sat: 'Friday - Saturday',
      hours_closed: 'Closed',
      map_title: 'How to find us',
      address_label: 'Address',
      phone_label: 'Phone',
      email_label: 'Email'
    },
    not_found: {
      title: 'Page not found (404)',
      desc: 'Sorry, the page you are looking for does not exist or has been moved.',
      btn: 'Go to homepage'
    },
    forbidden: {
      title: 'Access denied (403)',
      desc: 'You do not have permission to access this page.',
      btn: 'Go to homepage'
    },
    vidensbank: {
      title: 'Knowledge base',
      subtitle: 'Explore our articles and gain deeper insights into ADHD, autism, and mental health.',
      search_placeholder: 'Search articles...',
      all_categories: 'All categories',
      read_time: 'Read time',
      read_more: 'Read article',
      no_results: 'No articles found matching your search.',
      back_to_list: 'Back to knowledge base'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Language is strictly driven by the URL pathname (not localStorage)
  // Danish: / or /adhd or /vidensbank/...
  // English: /en or /en/adhd or /en/knowledge-base/...
  const isEn = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const lang = isEn ? 'en' : 'da';

  // Compute equivalent URL on both languages using semantic slug mapping
  const danishPath = toDanishPath(location.pathname);
  const englishPath = toEnglishPath(location.pathname);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem('clinic_lang', lang);
    } catch {
      // safe fallback
    }
  }, [lang]);

  // Navigate when explicitly changing language (e.g. from code or buttons)
  const setLang = (targetLang) => {
    if (targetLang === 'en' && !isEn) {
      navigate(englishPath + location.search + location.hash);
    } else if (targetLang === 'da' && isEn) {
      navigate(danishPath + location.search + location.hash);
    }
  };

  // Helper to localize any internal link path based on active language
  const localizePath = (path) => {
    if (!path || typeof path !== 'string') return path;
    if (
      path.startsWith('http://') ||
      path.startsWith('https://') ||
      path.startsWith('mailto:') ||
      path.startsWith('tel:') ||
      path.startsWith('#')
    ) {
      return path;
    }
    if (lang === 'en') {
      return toEnglishPath(path);
    }
    // lang === 'da'
    return toDanishPath(path);
  };

  const t = (path) => {
    const keys = path.split('.');
    const activeDict = (lang === 'en' ? translations.en : translations.da) || translations.da;
    let result = activeDict;
    for (const key of keys) {
      if (result && result[key] !== undefined) {
        result = result[key];
      } else {
        // Fallback to Danish if missing in English dictionary
        let fb = translations.da;
        for (const k of keys) {
          if (fb && fb[k] !== undefined) {
            fb = fb[k];
          } else {
            return path;
          }
        }
        return fb || path;
      }
    }
    return result;
  };

  const getLocalizedValue = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    if (lang === 'en') {
      return obj['en'] || obj['sv'] || obj['da'] || '';
    }
    return obj['da'] || obj['en'] || obj['sv'] || '';
  };

  return (
    <LanguageContext.Provider
      value={{
        lang,
        isEn,
        setLang,
        danishPath,
        englishPath,
        localizePath,
        t,
        getLocalizedValue
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
